import { pqcParameterSet } from '../lib/pqc-parameter-set';
import { CryptoPartsCell } from './crypto-parts-cell';
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, postCsv, ApiError, apiUrl, getAll } from '../api/client';
import { Modal, FormError } from './admin-ui';
import { Pagination } from './pagination';
import { RiskBadge } from './risk-badge';
import { KbLookupStatusBadge } from './kb-lookup-status-badge';
import type {
  ArtifactType, ImportSummary, UnifiedImportSummary, UnlinkedArtifact,
  ProjectAssetDetail,
  CryptoArtifactKey, CryptoArtifactCertificate, CryptoArtifactProtocol,
  CryptoArtifactCipherSuite, CryptoArtifactLibrary, CryptoArtifactStore,
  CryptoArtifactSignature,
  CryptoArtifactSshAlgorithm,
  CryptoArtifactAlgorithm,
} from '../api/types';
import { ArtifactForm, rowToForm } from './crypto-inventory-panel';

/**
 * Slice 8.7 — Crypto artifacts panel.
 *
 * Renders the per-asset cryptographic inventory for a project. Tabbed by
 * artifact type (key / certificate / protocol / cipher_suite / library /
 * store / signature). Each tab shows a table of rows for that type with
 * the artifact's classification (algorithm, KB status, risk level).
 *
 * Top-level actions:
 *   - "Import CSV" — opens a dialog to upload artifacts in bulk
 *
 * Per-row inline editing is deferred — the CSV import flow is the primary
 * data-entry path for v0.8. Single-row edits will arrive in 8.7b once the
 * customer has validated the CSV-first workflow.
 */

const TYPE_LABELS: Record<ArtifactType, string> = {
  key: 'Keys',
  certificate: 'Certificates',
  protocol: 'Protocols',
  cipher_suite: 'Cipher suites',
  library: 'Libraries',
  store: 'Key stores',
  signature: 'Signatures',
  ssh_algorithm: 'SSH algorithms',
  algorithm: 'Algorithms in code',
};

const TYPES: ArtifactType[] = ['key', 'certificate', 'protocol', 'cipher_suite', 'library', 'store', 'signature', 'ssh_algorithm', 'algorithm'];
/** SSH algorithms and algorithms in code arrive only through the unified CBOM import and are not edited by hand. */
type EditableType = Exclude<ArtifactType, 'ssh_algorithm' | 'algorithm'>;
const importOnly = (t: ArtifactType): t is 'ssh_algorithm' | 'algorithm' => t === 'ssh_algorithm' || t === 'algorithm';

// Per-type CSV header templates — shown to the user in the import dialog.
const CSV_TEMPLATES: Record<EditableType, { header: string; example: string }> = {
  key: {
    header: 'asset_id,name,algorithm_raw,key_type,key_length,curve_name,file_path,file_format,key_usage,notes',
    example: '<asset-uuid>,RSA signing key,RSA,Private,2048,,,PEM,signing,Production signing key',
  },
  certificate: {
    header: 'asset_id,name,signature_algorithm_raw,hashing_algorithm_raw,public_key_algorithm_raw,public_key_length,curve_name,serial_number,subject,issuer,notes',
    example: '<asset-uuid>,TLS cert,ECDSA,SHA-256,ECDSA,256,P-256,,CN=api.bank.com,CN=DigiCert,TLS server cert',
  },
  protocol: {
    header: 'asset_id,name,protocol_family,protocol_version_raw,endpoint,configuration_notes,notes',
    example: '<asset-uuid>,TLS endpoint,TLS,1.2,api.bank.com:443,,Needs upgrade to TLS 1.3',
  },
  cipher_suite: {
    header: 'asset_id,name,suite_raw,iana_code,kex_algorithm,authentication_algorithm,bulk_encryption_algorithm,mac_or_aead,notes',
    example: '<asset-uuid>,,TLS_ECDHE_RSA_WITH_AES_256_GCM_SHA384,0xC02F,ECDHE,RSA,AES-256-GCM,SHA-384,',
  },
  library: {
    header: 'asset_id,name,vendor,version_raw,install_path,is_fips_validated,has_pqc_support,end_of_life_date,notes',
    example: '<asset-uuid>,OpenSSL,OpenSSL Foundation,1.1.1u,/usr/lib/ssl,false,false,2023-09-11,',
  },
  store: {
    header: 'asset_id,name,store_type,vendor,fips_level,has_pqc_support,location,key_count,notes',
    example: '<asset-uuid>,Production HSM,HSM,Vaultline,Level_3,false,London DC,2048,',
  },
  signature: {
    header: 'asset_id,name,signature_purpose,signature_algorithm_raw,hashing_algorithm_raw,key_length,curve_name,notes',
    example: '<asset-uuid>,API JWT signer,JWT,RS256,SHA-256,2048,,Production API signing',
  },
};

// Unified CBOM CSV — the cross-product template exported by UCDI
// (GET /api/export/csv/:projectId). One file, every artifact type;
// artifact_type discriminates each row and asset_external_ref is the ONLY
// asset reference (unified Asset external_ref — never internal IDs).
const UNIFIED_CSV_TEMPLATE = {
  header: 'artifact_type,name,asset_external_ref,asset_name,algorithm,key_type,material_type,key_length,curve_name,format,encoding,state,signature_algorithm,hashing_algorithm,public_key_algorithm,public_key_length,serial_number,subject,issuer,issue_date,expiry_date,validity_status,cert_format,version,is_self_signed,san_domains,fingerprint_sha256,key_usage,extended_key_usage,protocol_type,protocol_version,key_exchange,authentication,encryption,mac,hash_algo,pfs,security,purl,cpe,primitive,execution_environment,implementation_platform,quantum_vulnerability,evidence_source,confidence_tier,ip_address,file_path,created_at',
  example: 'key,RSA,CBP-001,Core Banking Platform,RSA,private,private-key,2048,,PEM,,active,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,Vulnerable(Classic),fss_agent,High,,,',
};

interface PanelProps {
  projectId: string;
}

export function CryptoArtifactsPanel({ projectId }: PanelProps) {
  const [activeType, setActiveType] = useState<ArtifactType>('key');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [cbomDialogOpen, setCbomDialogOpen] = useState(false);
  const [unifiedDialogOpen, setUnifiedDialogOpen] = useState(false);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-display text-lg text-ink">Cryptographic artifacts</h3>
          <p className="text-sm text-ink-500 mt-1">
            Algorithms, keys, certificates, protocols, libraries, key stores,
            and signatures inventoried per asset. Risk classifications come
            from the bundled NIST/IETF Cryptography Knowledge Base.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setImportDialogOpen(true)}
            className="text-sm bg-accent text-white px-3 py-1.5 rounded hover:bg-accent-hover"
          >
            Import CSV
          </button>
          <button
            type="button"
            onClick={() => setCbomDialogOpen(true)}
            className="text-sm border border-blue-400 text-blue-700 bg-blue-50 px-3 py-1.5 rounded hover:bg-blue-100 transition-colors"
          >
            Import CBOM
          </button>
          <button
            type="button"
            onClick={() => setUnifiedDialogOpen(true)}
            className="text-sm border border-ink-300 text-ink-600 px-3 py-1.5 rounded hover:border-accent hover:text-accent transition-colors"
          >
            Import unified CSV
          </button>
        </div>
      </div>

      <div className="border-b border-ink-200 mb-4">
        <nav className="flex gap-1 -mb-px">
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveType(t)}
              className={`px-3 py-2 text-sm border-b-2 transition-colors ${
                activeType === t
                  ? 'border-accent text-ink font-medium'
                  : 'border-transparent text-ink-500 hover:text-ink'
              }`}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </nav>
      </div>

      <ArtifactTable key={activeType} projectId={projectId} type={activeType} />

      <UnlinkedArtifactsSection projectId={projectId} />

      {!importOnly(activeType) && (
        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          projectId={projectId}
          artifactType={activeType}
        />
      )}
      <CbomImportDialog
        open={cbomDialogOpen}
        onClose={() => setCbomDialogOpen(false)}
        projectId={projectId}
      />
      <UnifiedImportDialog
        open={unifiedDialogOpen}
        onClose={() => setUnifiedDialogOpen(false)}
        projectId={projectId}
      />
    </div>
  );
}

// =============================================================================
// Unlinked artifacts — imported without an asset match (cross-product
// interop). One asset dropdown per record; selecting an asset associates it
// IMMEDIATELY (no re-import).
// =============================================================================

function UnlinkedArtifactsSection({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const { data: unlinked } = useQuery<UnlinkedArtifact[]>({
    queryKey: ['crypto-artifacts-unlinked', projectId],
    queryFn: () => api.get<UnlinkedArtifact[]>(`/projects/${projectId}/crypto-artifacts/unlinked`),
  });

  const { data: assets } = useQuery<ProjectAssetDetail[]>({
    queryKey: ['project-assets-for-linking', projectId],
    queryFn: () => getAll<ProjectAssetDetail>(`/projects/${projectId}/assets?include=details`),
    enabled: (unlinked?.length ?? 0) > 0,
  });

  const linkMutation = useMutation({
    mutationFn: ({ artifact, assetId }: { artifact: UnlinkedArtifact; assetId: string }) =>
      api.patch(`/projects/${projectId}/crypto-artifacts/${artifact.artifact_type}/${artifact.id}/link`, { asset_id: assetId }),
    onSuccess: (_data, { artifact }) => {
      setRowErrors((prev) => { const n = { ...prev }; delete n[artifact.id]; return n; });
      queryClient.invalidateQueries({ queryKey: ['crypto-artifacts-unlinked', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crypto-artifacts', projectId, artifact.artifact_type] });
    },
    onError: (e, { artifact }) => {
      setRowErrors((prev) => ({ ...prev, [artifact.id]: e instanceof Error ? e.message : 'Link failed' }));
    },
  });

  if (!unlinked || unlinked.length === 0) return null;

  return (
    <div className="mt-6 border border-amber-300 bg-amber-50 rounded p-4">
      <h4 className="font-medium text-ink flex items-center gap-2">
        Unlinked artifacts
        <span className="text-xs font-normal bg-amber-200 text-amber-900 rounded-full px-2 py-0.5">{unlinked.length}</span>
      </h4>
      <p className="text-xs text-ink-500 mt-1 mb-3">
        These artifacts were imported without a matching asset — the exported
        external reference didn't match any asset enrolled in this project.
        Pick an asset to associate each record immediately.
      </p>
      {/* overflow-x-auto, not overflow-hidden: a wide table scrolls sideways instead of being cut off. */}
      <div className="border border-ink-200 rounded overflow-x-auto bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-ink-50 text-xs text-ink-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Type</th>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">External ref</th>
              <th className="text-left px-3 py-2 font-medium">Source</th>
              <th className="text-left px-3 py-2 font-medium">Risk</th>
              <th className="text-left px-3 py-2 font-medium">Link to asset</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {unlinked.map((row) => (
              <tr key={row.id}>
                <td className="px-3 py-2 text-ink-500">{TYPE_LABELS[row.artifact_type] ?? row.artifact_type}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.name ?? '—'}</td>
                <td className="px-3 py-2 font-mono text-xs text-ink-500">{row.asset_external_ref ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-ink-500">{row.source}</td>
                <td className="px-3 py-2"><RiskBadge level={row.risk_level} /></td>
                <td className="px-3 py-2">
                  <select
                    defaultValue=""
                    disabled={linkMutation.isPending}
                    onChange={(e) => {
                      const assetId = e.target.value;
                      if (assetId) linkMutation.mutate({ artifact: row, assetId });
                    }}
                    className="text-xs border border-ink-200 rounded px-2 py-1 bg-white text-ink max-w-[220px]"
                    aria-label={`Link ${row.name ?? row.id} to asset`}
                  >
                    <option value="">Select asset…</option>
                    {(assets ?? []).map((a) => (
                      <option key={a.asset_id} value={a.asset_id}>
                        {a.asset_name}{a.asset_external_ref ? ` (${a.asset_external_ref})` : ''}
                      </option>
                    ))}
                  </select>
                  {rowErrors[row.id] && (
                    <div className="text-[11px] text-risk-critical mt-1">{rowErrors[row.id]}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Unified CBOM CSV import — the cross-product template exported by UCDI.
// Asset matching is by asset_external_ref only; unmatched rows import as
// Unlinked and appear in the section above.
// =============================================================================

interface UnifiedImportDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
}

function UnifiedImportDialog({ open, onClose, projectId }: UnifiedImportDialogProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const fileRef = useRef<File | null>(null);
  const [mode, setMode] = useState<'replace' | 'append'>('append');
  const [summary, setSummary] = useState<UnifiedImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    fileRef.current = file;
    setFileName(file.name);
    setSummary(null);
    setError(null);
    e.target.value = '';
  }, []);

  const importMutation = useMutation({
    mutationFn: async ({ mode: m }: { mode: 'replace' | 'append' }) => {
      const file = fileRef.current;
      if (!file) throw new Error('No file selected.');
      const csvText = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve((ev.target?.result as string) ?? '');
        reader.onerror = () => reject(new Error('Failed to read file.'));
        reader.readAsText(file, 'utf-8');
      });
      if (!csvText.trim()) throw new Error('File is empty.');
      abortControllerRef.current = new AbortController();
      const path = `/projects/${projectId}/crypto-artifacts/unified/import?mode=${m}`;
      return postCsv<UnifiedImportSummary>(path, csvText, abortControllerRef.current.signal);
    },
    onSuccess: (data) => {
      setSummary(data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['crypto-artifacts-unlinked', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crypto-artifacts', projectId] });
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : 'Import failed';
      setError(msg === 'Import cancelled.' ? null : msg);
      setSummary(null);
    },
    onSettled: () => { abortControllerRef.current = null; },
  });

  function handleClose() {
    abortControllerRef.current?.abort();
    fileRef.current = null;
    setFileName(null);
    setSummary(null);
    setError(null);
    onClose();
  }

  return (
    <Modal open={open} onClose={handleClose} title="Import unified CBOM CSV" widthClass="max-w-3xl">
      <div className="space-y-4">
        <p className="text-sm text-ink-500">
          One CSV covering the complete cryptographic inventory — certificates,
          keys, protocols, cipher suites, and libraries — as exported by UCDI.
          Rows are matched to assets by <strong>asset_external_ref</strong> (the
          unified cross-product asset identifier). Rows without a match are
          imported as <em>Unlinked</em> and can be associated manually — the
          import never fails because an asset didn't match.
        </p>

        <details className="text-xs bg-ink-50 rounded p-3">
          <summary className="cursor-pointer text-ink-500 font-medium flex items-center justify-between">
            <span>▶ CSV format reference</span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const csv = `${UNIFIED_CSV_TEMPLATE.header}\n${UNIFIED_CSV_TEMPLATE.example}\n`;
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'qram-unified-cbom-template.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="text-accent hover:underline text-[11px] ml-4 font-normal"
            >
              Download template
            </button>
          </summary>
          <div className="mt-2 space-y-2">
            <code className="block bg-white border border-ink-200 rounded p-2 font-mono text-[11px] overflow-x-auto whitespace-pre">
              {UNIFIED_CSV_TEMPLATE.header}
            </code>
            <p className="text-ink-500">
              <strong>artifact_type</strong> (certificate | key | protocol |
              cipher_suite | library) selects which columns apply to a row;
              inapplicable cells stay empty. <strong>asset_external_ref</strong>{' '}
              is the only asset reference — internal IDs from other products
              are never used.
            </p>
          </div>
        </details>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setSummary(null);
            if (!fileRef.current) { setError('No file selected. Please upload a CSV file before importing.'); return; }
            importMutation.mutate({ mode });
          }}
          className="space-y-4"
        >
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium text-ink-600">Mode:</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={mode === 'append'} onChange={() => setMode('append')} />
              <span>Append <span className="text-xs text-ink-400">(add to existing)</span></span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} />
              <span>Replace <span className="text-xs text-ink-400">(delete existing, then import)</span></span>
            </label>
          </div>

          <div
            className={`border-2 border-dashed rounded-sm p-6 text-center transition-colors ${
              fileName ? 'border-green-300 bg-green-50' : 'border-ink-200 bg-ink-50 hover:border-accent'
            }`}
          >
            {fileName ? (
              <div className="space-y-2">
                <div className="text-xl">✓</div>
                <div className="text-sm font-medium text-green-700">{fileName}</div>
                <button
                  type="button"
                  onClick={() => { fileRef.current = null; setFileName(null); setSummary(null); setError(null); }}
                  className="text-xs text-ink-400 hover:text-ink-700 underline"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-2xl text-ink-300">↑</div>
                <div className="text-sm text-ink-500">Select the unified CBOM CSV exported by UCDI</div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm border border-ink-300 rounded px-4 py-1.5 text-ink-600 hover:border-accent hover:text-accent transition-colors"
                >
                  Choose file…
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>

          <FormError message={error} />

          <div className="flex justify-between items-center pt-2 border-t border-ink-100">
            {summary ? (
              // Post-import state: the run is complete, so there is no "Import"
              // button to click by accident. Re-running requires an explicit
              // "Import another file", which clears the previous result first.
              <>
                <button
                  type="button"
                  onClick={() => { fileRef.current = null; setFileName(null); setSummary(null); setError(null); }}
                  className="text-sm text-ink-500 hover:text-ink underline"
                >
                  Import another file
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={handleClose} className="text-sm text-ink-500 hover:text-ink">
                  Close
                </button>
                <button
                  type="submit"
                  disabled={!fileName || importMutation.isPending}
                  className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover disabled:opacity-50"
                >
                  {importMutation.isPending ? 'Importing…' : 'Import'}
                </button>
              </>
            )}
          </div>
        </form>

        {summary && (
          <div className="border border-ink-200 rounded p-4 bg-ink-50 space-y-3">
            <h4 className="font-medium text-ink">Import results</h4>
            {/* New + updated + merged + failed always equals the rows in the file. */}
            <div className="grid grid-cols-4 lg:grid-cols-7 gap-4 text-sm">
              <div>
                <div className="text-ink-500 text-xs">Total rows</div>
                <div className="font-mono text-lg">{summary.total_rows}</div>
              </div>
              <div>
                <div className="text-ink-500 text-xs">New</div>
                <div className="font-mono text-lg text-green-700">{summary.imported}</div>
              </div>
              <div>
                <div className="text-ink-500 text-xs">Updated</div>
                <div className="font-mono text-lg">{summary.updated ?? 0}</div>
              </div>
              <div>
                <div className="text-ink-500 text-xs">Merged</div>
                <div className="font-mono text-lg">{summary.merged ?? 0}</div>
              </div>
              <div>
                <div className="text-ink-500 text-xs">Linked</div>
                <div className="font-mono text-lg">{summary.linked}</div>
              </div>
              <div>
                <div className="text-ink-500 text-xs">Unlinked</div>
                <div className="font-mono text-lg text-amber-700">{summary.unlinked}</div>
              </div>
              <div>
                <div className="text-ink-500 text-xs">Failed</div>
                <div className="font-mono text-lg text-risk-critical">{summary.failed}</div>
              </div>
            </div>
            <div className="text-xs text-ink-500">
              Stored by type: {Object.entries(summary.by_type).filter(([, n]) => n > 0).map(([t, n]) => `${t === 'code_note' ? 'Code notes (not artifacts)' : TYPE_LABELS[t as ArtifactType] ?? t}: ${n}`).join(' · ') || '—'}
              {(summary.rated_from_import ?? 0) > 0 && (
                <> · {summary.rated_from_import} rated from the imported assessment</>
              )}
            </div>
            {(summary.notices ?? []).length > 0 && (
              <ul className="text-xs text-ink-700 bg-white border border-ink-200 rounded px-3 py-2 space-y-1 list-disc list-inside">
                {(summary.notices ?? []).map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
            {summary.unlinked > 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                {summary.unlinked} artifact{summary.unlinked === 1 ? '' : 's'} imported without an
                asset match — associate them in the "Unlinked artifacts" section below the table.
              </p>
            )}
            {summary.parse_errors.length > 0 && (
              <div className="text-xs text-risk-critical bg-red-50 border border-red-200 rounded px-3 py-2 space-y-1">
                <p className="font-medium">
                  {summary.parse_errors.length} line{summary.parse_errors.length === 1 ? '' : 's'} could
                  not be read and {summary.parse_errors.length === 1 ? 'was' : 'were'} skipped — these
                  are NOT counted in “Total rows”, so the file contained more records than were imported.
                </p>
                <div className="max-h-32 overflow-y-auto font-mono">
                  {summary.parse_errors.slice(0, 20).map((e, i) => (
                    <div key={i}>Line {e.row_num}: {e.message}</div>
                  ))}
                  {summary.parse_errors.length > 20 && (
                    <div>…and {summary.parse_errors.length - 20} more</div>
                  )}
                </div>
              </div>
            )}
            {summary.results.some((r) => r.status === 'failed') && (
              <div className="max-h-40 overflow-y-auto text-xs">
                {summary.results.filter((r) => r.status === 'failed').map((r) => (
                  <div key={r.row_num} className="text-risk-critical">
                    Row {r.row_num}: {(r.errors ?? []).join('; ')}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// =============================================================================
// Per-type table — shared shell, per-type column definitions
// =============================================================================

interface ArtifactTableProps {
  projectId: string;
  type: ArtifactType;
}

interface ListResponse<T> { data: T[]; pagination: { total: number; limit: number; offset: number } }

function ArtifactTable({ projectId, type }: ArtifactTableProps) {
  const [assetFilter, setAssetFilter] = useState<string>('');
  const [pageSize, setPageSize] = useState(100);
  const [offset, setOffset] = useState(0);
  const [editRow, setEditRow] = useState<Record<string, unknown> | null>(null);

  // Changing the asset filter is a new result set — always start from page 1.
  const changeFilter = (next: string) => { setAssetFilter(next); setOffset(0); };

  // Server-side pagination. The list endpoint already accepts limit/offset and
  // returns { data, pagination:{ total, limit, offset } }; previously the UI
  // sent neither, so it only ever showed the first page (server default 200)
  // with no way to reach the rest of a large inventory.
  const params = new URLSearchParams();
  if (assetFilter) params.set('asset_id', assetFilter);
  params.set('limit', String(pageSize));
  params.set('offset', String(offset));
  const queryUrl = apiUrl(`/projects/${projectId}/crypto-artifacts/${type}?${params.toString()}`);

  const { data, isLoading, isError, error } = useQuery<ListResponse<unknown>>({
    queryKey: ['crypto-artifacts', projectId, type, assetFilter, offset],
    queryFn: async () => {
      // Use raw fetch so we get the full envelope (data + pagination)
      const res = await fetch(queryUrl, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new ApiError(res.status, body?.error?.message ?? `HTTP ${res.status}`, body);
      }
      return res.json();
    },
  });

  const rows = (data?.data ?? []) as Array<{ asset_id: string; asset_name?: string }>;
  const total = data?.pagination?.total ?? 0;

  // Safety net: if the current offset lands past the end of the result set
  // (e.g. the set shrank, or a smaller type is shown), snap back to the last
  // valid page instead of showing an empty "1201–1200 of 556" page.
  useEffect(() => {
    if (total > 0 && offset >= total) {
      setOffset(Math.max(0, (Math.ceil(total / pageSize) - 1) * pageSize));
    }
  }, [total, offset, pageSize]);

  // Build a unique-asset-list for the filter dropdown from the current rows.
  // (When a filter is active, this will be a single-asset list — that's fine;
  // the dropdown still lets the user clear back to "All assets".)
  // IMPORTANT: useMemo MUST come before any early return. Putting it after a
  // conditional `return` violates React's Rules of Hooks and causes hook-count
  // mismatches between renders (the symptom is a silently-blank Cryptography
  // tab when navigating to it from a loading→loaded transition).
  const assetOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (r.asset_id && !seen.has(r.asset_id)) {
        seen.set(r.asset_id, r.asset_name ?? r.asset_id);
      }
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  if (isLoading) {
    return <div className="text-sm text-ink-500 italic py-4">Loading {TYPE_LABELS[type].toLowerCase()}…</div>;
  }
  if (isError) {
    return <FormError message={(error as Error)?.message ?? 'Failed to load artifacts'} />;
  }

  if (rows.length === 0 && !assetFilter && offset === 0) {
    return (
      <div className="border border-dashed border-ink-200 rounded p-6 text-center">
        <p className="text-ink-500 text-sm">
          No {TYPE_LABELS[type].toLowerCase()} inventoried for this project yet.
        </p>
        <p className="text-ink-300 text-xs mt-2">
          Use "Import CSV" above to bulk-import an inventory.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Pagination
            pageIndex={pageSize > 0 ? Math.floor(offset / pageSize) : 0}
            pageCount={Math.max(1, Math.ceil(total / pageSize))}
            onPageChange={(i) => setOffset(i * pageSize)}
            pageSize={pageSize}
            onPageSizeChange={(s) => { setPageSize(s); setOffset(0); }}
            totalItems={total}
            itemLabel={TYPE_LABELS[type].toLowerCase()}
            disabled={isLoading}
          />
          {assetFilter && (
            <button
              type="button"
              onClick={() => changeFilter('')}
              className="text-xs text-accent hover:underline"
            >
              Clear filter
            </button>
          )}
        </div>
        <label className="flex items-center gap-2 text-xs text-ink-500">
          Filter by asset:
          <select
            value={assetFilter}
            onChange={(e) => changeFilter(e.target.value)}
            className="text-xs border border-ink-200 rounded px-2 py-1 bg-white text-ink"
            aria-label="Filter by asset"
          >
            <option value="">All assets</option>
            {assetOptions.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </label>
      </div>
      {editRow && !importOnly(type) && (
        <div className="mb-4">
          <ArtifactForm
            mode="edit"
            projectId={projectId}
            assetId={String(editRow['asset_id'] ?? '')}
            fixedType={type}
            initial={rowToForm(type, editRow)}
            artifactId={String(editRow['id'] ?? '')}
            invalidateKey={['crypto-artifacts', projectId, type]}
            onClose={() => setEditRow(null)}
          />
        </div>
      )}
      {rows.length === 0 ? (
        <div className="border border-dashed border-ink-200 rounded p-6 text-center text-ink-500 text-sm">
          No {TYPE_LABELS[type].toLowerCase()} for the selected asset.
        </div>
      ) : (
        <div className="border border-ink-200 rounded overflow-x-auto">
          {/* overflow-x-auto, not overflow-hidden: a wide table scrolls sideways instead of being cut off. */}
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50 text-xs text-ink-500 uppercase tracking-wide">
              <ColumnHeaders type={type} />
            </thead>
            <tbody className="divide-y divide-ink-100">
              {rows.map((row, i) => (
                <ArtifactRow key={i} type={type} row={row as Record<string, unknown>} onEdit={setEditRow} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ColumnHeaders({ type }: { type: ArtifactType }) {
  const COLS: Record<ArtifactType, string[]> = {
    key:          ['Asset', 'Name', 'Algorithm', 'Parameter set', 'Key type', 'Length', 'Curve', 'Fingerprint', 'KB status', 'Risk'],
    certificate:  ['Asset', 'Name / Subject', 'Sig alg', 'Hash', 'Pub-key alg', 'Length', 'KB status', 'Risk'],
    protocol:     ['Asset', 'Name', 'Family', 'Version', 'State', 'SNMPv3 level', 'Parts (each rated)', 'KB status', 'Risk'],
    cipher_suite: ['Asset', 'Name', 'IANA name', 'TLS versions', 'State', 'Parts (each rated)', 'KB status', 'Risk'],
    library:      ['Asset', 'Library', 'Vendor', 'Version', 'FIPS', 'PQC', 'EOL', 'KB status', 'Risk'],
    store:        ['Asset', 'Name', 'Type', 'Vendor', 'FIPS', 'KB status', 'Risk'],
    signature:    ['Asset', 'Name', 'Purpose', 'Sig alg', 'Hash', 'Length', 'KB status', 'Risk'],
    ssh_algorithm: ['Asset', 'Algorithm', 'Role', 'SSH version', 'Inventory rating', 'Risk'],
    algorithm:    ['Asset', 'Algorithm', 'KB name', 'Use', 'File', 'Seen', 'Inventory rating', 'KB status', 'Risk'],
  };
  return (
    <tr>
      {COLS[type].map((c) => (
        <th key={c} className="text-left px-3 py-2 font-medium">{c}</th>
      ))}
      <th className="text-right px-3 py-2 font-medium">Actions</th>
    </tr>
  );
}

function AssetCell({ row }: { row: { asset_name?: string; asset_type_code?: string } }) {
  return (
    <td className="px-3 py-2 text-ink whitespace-nowrap">
      <div className="font-medium truncate max-w-xs" title={row.asset_name ?? ''}>
        {row.asset_name ?? <em className="text-ink-300">unknown</em>}
      </div>
      {row.asset_type_code && (
        <div className="text-xs text-ink-400 font-mono">{row.asset_type_code}</div>
      )}
    </td>
  );
}

function ArtifactRow({ type, row, onEdit }: { type: ArtifactType; row: Record<string, unknown>; onEdit: (row: Record<string, unknown>) => void }) {
  // Each artifact type renders different columns; the values themselves
  // come from the row record. We keep this pure and table-driven.
  const r = row as unknown as
    & CryptoArtifactKey & CryptoArtifactCertificate & CryptoArtifactProtocol
    & CryptoArtifactCipherSuite & CryptoArtifactLibrary & CryptoArtifactStore
    & CryptoArtifactSignature & CryptoArtifactSshAlgorithm & CryptoArtifactAlgorithm
    & { asset_name?: string; asset_type_code?: string };

  let cells: JSX.Element | null = null;
  switch (type) {
    case 'key':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 text-ink">{r.name ?? <em className="text-ink-300">—</em>}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.algorithm_canonical ?? r.algorithm_raw}</td>
          <td className="px-3 py-2 font-mono text-xs">{pqcParameterSet(r.algorithm_canonical ?? r.algorithm_raw, r.key_length) ?? '—'}</td>
          <td className="px-3 py-2 text-ink-500">{r.key_type}</td>
          <td className="px-3 py-2 font-mono text-xs">{pqcParameterSet(r.algorithm_canonical ?? r.algorithm_raw, r.key_length) ? '—' : (r.key_length ?? '—')}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.curve_name ?? '—'}</td>
          <td className="px-3 py-2 font-mono text-xs" title={r.fingerprint_sha256 ?? undefined}>{r.fingerprint_sha256 ? `${r.fingerprint_sha256.slice(0, 12)}…` : '—'}</td>
          <td className="px-3 py-2"><KbLookupStatusBadge status={r.kb_lookup_status} /></td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
    case 'certificate':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 text-ink">
            <div className="truncate max-w-xs" title={r.subject ?? r.name ?? ''}>
              {r.name ?? r.subject ?? <em className="text-ink-300">—</em>}
            </div>
          </td>
          <td className="px-3 py-2 font-mono text-xs">{r.signature_algorithm_raw}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.hashing_algorithm_raw ?? '—'}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.public_key_algorithm_raw}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.public_key_length ?? '—'}</td>
          <td className="px-3 py-2"><KbLookupStatusBadge status={r.kb_lookup_status} /></td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
    case 'protocol':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 text-ink">{r.name ?? <em className="text-ink-300">—</em>}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.protocol_family}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.protocol_version_raw}</td>
          <td className="px-3 py-2 text-xs">{r.configured_state ?? '—'}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.snmp_security_level ?? '—'}</td>
          <td className="px-3 py-2"><CryptoPartsCell row={r as unknown as Record<string, unknown>} /></td>
          <td className="px-3 py-2"><KbLookupStatusBadge status={r.kb_lookup_status} /></td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
    case 'cipher_suite':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 text-ink">{r.name ?? <em className="text-ink-300">—</em>}</td>
          <td className="px-3 py-2 font-mono text-xs">
            <div className="truncate max-w-md" title={r.suite_raw}>{r.suite_canonical ?? r.suite_raw}</div>
          </td>
          <td className="px-3 py-2 font-mono text-xs">{r.tls_version ?? '—'}</td>
          <td className="px-3 py-2 text-xs">{r.configured_state ?? '—'}</td>
          {/* Key exchange, authentication, bulk cipher and MAC, each with its own quantum view: the suite is rated by its weakest part. */}
          <td className="px-3 py-2"><CryptoPartsCell row={r as unknown as Record<string, unknown>} /></td>
          <td className="px-3 py-2"><KbLookupStatusBadge status={r.kb_lookup_status} /></td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
    case 'ssh_algorithm':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 font-mono text-xs">{r.algorithm}</td>
          <td className="px-3 py-2 text-ink-500">{String(r.algorithm_role).replace(/_/g, ' ')}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.protocol_version ?? '—'}</td>
          <td className="px-3 py-2 text-xs">{Number(r.classically_broken) ? 'Broken classically' : String(r.quantum_vulnerability).replace(/_/g, ' ')}</td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
    case 'algorithm':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 font-mono text-xs">{r.algorithm}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.algorithm_canonical ?? '—'}</td>
          <td className="px-3 py-2 text-xs text-ink-500">{r.usage_kind ? String(r.usage_kind).replace(/-usage$/, '') : '—'}</td>
          <td className="px-3 py-2 font-mono text-xs"><div className="truncate max-w-xs" title={r.file_path ?? undefined}>{r.file_path ?? '—'}</div></td>
          <td className="px-3 py-2 font-mono text-xs">{r.observation_count ?? '—'}</td>
          <td className="px-3 py-2 text-xs">{Number(r.classically_broken) ? 'Broken classically' : String(r.quantum_vulnerability).replace(/_/g, ' ')}</td>
          <td className="px-3 py-2"><KbLookupStatusBadge status={r.kb_lookup_status} /></td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
    case 'library':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 text-ink">{r.name}</td>
          <td className="px-3 py-2 text-ink-500">{r.vendor ?? '—'}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.version_raw}</td>
          <td className="px-3 py-2 text-xs">{r.is_fips_validated ? '✓' : '—'}</td>
          <td className="px-3 py-2 text-xs">{r.has_pqc_support ? '✓' : '—'}</td>
          <td className="px-3 py-2 text-xs">{r.end_of_life_date ?? '—'}</td>
          <td className="px-3 py-2"><KbLookupStatusBadge status={r.kb_lookup_status} /></td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
    case 'store':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 text-ink">{r.name}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.store_type}</td>
          <td className="px-3 py-2 text-ink-500">{r.vendor ?? '—'}</td>
          <td className="px-3 py-2 text-xs">{r.fips_level}</td>
          <td className="px-3 py-2"><KbLookupStatusBadge status={r.kb_lookup_status} /></td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
    case 'signature':
      cells = (
        <>
          <AssetCell row={r} />
          <td className="px-3 py-2 text-ink">{r.name ?? <em className="text-ink-300">—</em>}</td>
          <td className="px-3 py-2 text-xs">{r.signature_purpose}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.signature_algorithm_raw}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.hashing_algorithm_raw ?? '—'}</td>
          <td className="px-3 py-2 font-mono text-xs">{r.key_length ?? '—'}</td>
          <td className="px-3 py-2"><KbLookupStatusBadge status={r.kb_lookup_status} /></td>
          <td className="px-3 py-2"><RiskBadge level={r.risk_level} size="xs" /></td>
        </>
      );
      break;
  }

  return (
    <tr className="hover:bg-ink-50">
      {cells}
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {!importOnly(type) && <button
          type="button"
          className="text-xs text-ink-700 border border-ink-200 rounded px-2 py-1 hover:bg-white"
          onClick={() => onEdit(row)}
        >
          Edit
        </button>}
      </td>
    </tr>
  );
}

// =============================================================================
// CSV import dialog
// =============================================================================

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  artifactType: EditableType;
}

function ImportDialog({ open, onClose, projectId, artifactType }: ImportDialogProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  // Store the File object itself — reading happens inside mutationFn
  // so the content is guaranteed ready when postCsv is called.
  const fileRef = useRef<File | null>(null);
  const [mode, setMode] = useState<'replace' | 'append'>('append') // Replace deletes the project's rows of this type; never the default;
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // AbortController for in-flight import — user can cancel at any time.
  const abortControllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const queryClient = useQueryClient();
  const template = CSV_TEMPLATES[artifactType];
  const hasFile = !!fileName;

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    fileRef.current = file;
    setFileName(file.name);
    setSummary(null);
    setError(null);
    e.target.value = '';
  }, []);

  const importMutation = useMutation({
    mutationFn: async ({ mode: m }: { mode: 'replace' | 'append' }) => {
      const file = fileRef.current;
      if (!file) throw new Error('No file selected.');
      // Read file content as text inside the mutation — guaranteed async-safe
      const csvText = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve((ev.target?.result as string) ?? '');
        reader.onerror = () => reject(new Error('Failed to read file.'));
        reader.readAsText(file, 'utf-8');
      });
      if (!csvText.trim()) throw new Error('File is empty.');
      // Create a fresh AbortController for this import so the user can cancel.
      abortControllerRef.current = new AbortController();
      const path = `/projects/${projectId}/crypto-artifacts/${artifactType}/import?mode=${m}`;
      return postCsv<ImportSummary>(path, csvText, abortControllerRef.current.signal);
    },
    onSuccess: (data) => {
      setSummary(data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['crypto-artifacts', projectId, artifactType] });
    },
    onError: (e) => {
      // Don't surface an error message for user-initiated cancels
      const msg = e instanceof Error ? e.message : 'Import failed';
      if (msg === 'Import cancelled.') {
        setError(null);
      } else {
        setError(msg);
      }
      setSummary(null);
    },
    onSettled: () => {
      abortControllerRef.current = null;
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setElapsedSecs(0);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSummary(null);
    if (!fileRef.current) {
      setError('No file selected. Please upload a CSV file before importing.');
      return;
    }
    setElapsedSecs(0);
    timerRef.current = setInterval(() => setElapsedSecs((s) => s + 1), 1000);
    importMutation.mutate({ mode });
  }

  function handleCancel() {
    abortControllerRef.current?.abort();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setElapsedSecs(0);
  }

  function handleClose() {
    // Cancel any in-flight import before closing
    abortControllerRef.current?.abort();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    fileRef.current = null;
    setFileName(null);
    setSummary(null);
    setError(null);
    setElapsedSecs(0);
    onClose();
  }

  return (
    <Modal open={open} onClose={handleClose} title={`Import ${TYPE_LABELS[artifactType].toLowerCase()} from CSV`} widthClass="max-w-3xl">
      <div className="space-y-4">

        {/* Format reference — collapsed by default */}
        {template && (
          <details className="text-xs bg-ink-50 rounded p-3">
            <summary className="cursor-pointer text-ink-500 font-medium flex items-center justify-between">
              <span>▶ CSV format reference</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const csv = `${template.header}\n${template.example}\n`;
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `qram-${artifactType}-template.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="text-accent hover:underline text-[11px] ml-4 font-normal"
              >
                Download template
              </button>
            </summary>
            <div className="mt-2 space-y-2">
              <div>
                <div className="text-ink-300 mb-1">Columns (required, case-sensitive):</div>
                <code className="block bg-white border border-ink-200 rounded p-2 font-mono text-[11px] overflow-x-auto">
                  {template.header}
                </code>
              </div>
              <p className="text-ink-500">
                <strong>asset_id</strong> must be the UUID of an asset enrolled in this project
                (visible in the Assets tab URL when you open an asset).
                Algorithm names are normalised before KB lookup — RSA-2048, RSA2048, and rsa 2048
                all resolve to the same canonical entry.
              </p>
            </div>
          </details>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Mode selector */}
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium text-ink-600">Mode:</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} />
              <span>Replace <span className="text-xs text-ink-400">(replaces this type only if every row is valid)</span></span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={mode === 'append'} onChange={() => setMode('append')} />
              <span>Append <span className="text-xs text-ink-400">(add to existing)</span></span>
            </label>
          </div>

          {/* File upload area — no raw CSV textarea */}
          <div
            className={`border-2 border-dashed rounded-sm p-6 text-center transition-colors ${
              hasFile ? 'border-green-300 bg-green-50' : 'border-ink-200 bg-ink-50 hover:border-accent'
            }`}
          >
            {hasFile ? (
              <div className="space-y-2">
                <div className="text-xl">✓</div>
                <div className="text-sm font-medium text-green-700">{fileName}</div>
                <div className="text-xs text-ink-500">Ready to import</div>
                <button
                  type="button"
                  onClick={() => { fileRef.current = null; setFileName(null); setSummary(null); setError(null); }}
                  className="text-xs text-ink-400 hover:text-ink-700 underline"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-2xl text-ink-300">↑</div>
                <div className="text-sm text-ink-500">Select a CSV file to import</div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm border border-ink-300 rounded px-4 py-1.5 text-ink-600 hover:border-accent hover:text-accent transition-colors"
                >
                  Choose file…
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>

          <FormError message={error} />

          {/* Elapsed time hint while import is running */}
          {importMutation.isPending && (
            <div className="flex items-center gap-2 text-xs text-ink-500 bg-ink-50 rounded px-3 py-2">
              <span className="inline-block w-3 h-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              <span>
                Processing KB lookups… {elapsedSecs > 0 && `(${elapsedSecs}s)`}
              </span>
              <span className="text-ink-300">·</span>
              <span>Large files may take up to a minute per 50 rows.</span>
            </div>
          )}

          <div className="flex justify-between items-center pt-2 border-t border-ink-100">
            {summary ? (
              // Post-import state: no "Import" button to click by accident.
              // Re-running requires an explicit "Import another file".
              <>
                <button
                  type="button"
                  onClick={() => { fileRef.current = null; setFileName(null); setSummary(null); setError(null); }}
                  className="text-sm text-ink-500 hover:text-ink underline"
                >
                  Import another file
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                {importMutation.isPending ? (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="text-sm text-risk-critical hover:underline"
                  >
                    Cancel import
                  </button>
                ) : (
                  <button type="button" onClick={handleClose} className="text-sm text-ink-500 hover:text-ink">
                    Close
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!hasFile || importMutation.isPending}
                  className="text-sm bg-accent text-white px-4 py-2 rounded hover:bg-accent-hover disabled:opacity-50"
                >
                  {importMutation.isPending ? 'Importing…' : 'Import'}
                </button>
              </>
            )}
          </div>
        </form>

        {summary && <ImportResultSummary summary={summary} />}
      </div>
    </Modal>
  );
}

function ImportResultSummary({ summary }: { summary: ImportSummary }) {
  const failedRows = summary.results.filter((r) => r.status === 'failed');

  const kbStatusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of summary.results) {
      if (r.status !== 'imported' || !r.kb_lookup_status) continue;
      counts[r.kb_lookup_status] = (counts[r.kb_lookup_status] ?? 0) + 1;
    }
    return counts;
  }, [summary]);

  return (
    <div className="border border-ink-200 rounded p-4 bg-ink-50 space-y-3">
      <h4 className="font-medium text-ink">Import results</h4>
      {summary.notices?.map((n) => (
        <p key={n} className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="import-notice">{n}</p>
      ))}
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-ink-500 text-xs">Total rows</div>
          <div className="font-mono text-lg">{summary.total_rows}</div>
        </div>
        <div>
          <div className="text-ink-500 text-xs">Imported</div>
          <div className="font-mono text-lg text-risk-no_risk">{summary.imported}</div>
        </div>
        <div>
          <div className="text-ink-500 text-xs">Failed</div>
          <div className="font-mono text-lg text-risk-critical">{summary.failed}</div>
        </div>
      </div>

      {Object.keys(kbStatusCounts).length > 0 && (
        <div className="text-xs space-y-1">
          <div className="text-ink-500">KB classification of imported rows:</div>
          <div className="flex gap-3">
            {Object.entries(kbStatusCounts).map(([status, count]) => (
              <span key={status} className="font-mono">
                {status}: <strong>{count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {summary.parse_errors.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-risk-high font-medium">
            {summary.parse_errors.length} parse error(s)
          </summary>
          <ul className="mt-2 space-y-1 font-mono">
            {summary.parse_errors.slice(0, 10).map((e, i) => (
              <li key={i}>row {e.row_num}: {e.message}</li>
            ))}
          </ul>
        </details>
      )}

      {failedRows.length > 0 && (
        <details className="text-xs" open>
          <summary className="cursor-pointer text-risk-critical font-medium">
            {failedRows.length} failed row(s)
          </summary>
          <ul className="mt-2 space-y-1 font-mono">
            {failedRows.slice(0, 20).map((r) => (
              <li key={r.row_num}>
                row {r.row_num}: {r.errors?.join('; ')}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
// =============================================================================
// CbomImportDialog — CycloneDX CBOM JSON import
// =============================================================================

interface CbomImportDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
}

interface CbomImportSummary {
  cbom_name: string | null;
  cbom_version: string | null;
  total_components: number;
  imported_keys: number;
  imported_certificates: number;
  imported_protocols: number;
  imported_cipher_suites: number;
  imported_libraries: number;
  imported_ssh_algorithms?: number;
  total_imported: number;
  imported_unlinked?: number;
  devices?: number;
  devices_matched?: number;
  devices_unmatched?: string[];
  failed: number;
  skipped: number;
  errors: Array<{ component: string; reason: string }>;
  mode: string;
}

function CbomImportDialog({ open, onClose, projectId }: CbomImportDialogProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [mode, setMode] = useState<'replace' | 'append'>('append');
  const [summary, setSummary] = useState<CbomImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cbomBodyRef = useRef<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Fetch enrolled assets so the user can see which asset IDs to use in the CBOM
  // The detailed rows name the asset asset_id / asset_name (not id / name), and every page is read.
  const { data: assetsData } = useQuery<ProjectAssetDetail[]>({
    queryKey: ['project-assets-cbom', projectId],
    queryFn: () => getAll<ProjectAssetDetail>(`/projects/${projectId}/assets?include=details`).catch(() => []),
    enabled: open,
    staleTime: 30_000,
  });
  const enrolledAssets = (assetsData ?? []).map((a) => ({ id: a.asset_id, name: a.asset_name }));

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setSummary(null);
    setError(null);
    const reader = new FileReader();
    reader.onload = (ev) => { cbomBodyRef.current = ev.target?.result as string ?? ''; };
    reader.onerror = () => setError('Failed to read file.');
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  }, []);

  const importMutation = useMutation({
    mutationFn: async () => {
      const body = cbomBodyRef.current;
      if (!body.trim()) throw new Error('No file selected.');
      const res = await fetch(apiUrl(`/projects/${projectId}/crypto-artifacts/cbom/import?mode=${mode}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: 'Import failed' } }));
        throw new Error(err?.error?.message ?? 'Import failed');
      }
      const data = await res.json() as { data: CbomImportSummary };
      return data.data;
    },
    onSuccess: (data) => {
      setSummary(data);
      setError(null);
      // Invalidate all artifact type queries so the tabs refresh
      queryClient.invalidateQueries({ queryKey: ['crypto-artifacts', projectId] });
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'Import failed');
      setSummary(null);
    },
  });

  function handleClose() {
    cbomBodyRef.current = '';
    setFileName(null);
    setSummary(null);
    setError(null);
    onClose();
  }

  const hasFile = !!fileName;

  return (
    <Modal open={open} onClose={handleClose} title="Import CBOM (CycloneDX)" widthClass="max-w-2xl">
      <div className="space-y-4">

        {/* Explainer */}
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-800">
          <div className="font-medium mb-1">CycloneDX CBOM import</div>
          Accepts a CycloneDX 1.4+ Cryptography Bill of Materials in <strong>JSON</strong> format.
          Keys, certificates, protocols, cipher suites, and libraries are imported and KB-enriched automatically.
          Each component must have a <code className="font-mono bg-blue-100 px-1">qram:asset_id</code> property
          matching an enrolled asset UUID below, or a <code className="font-mono bg-blue-100 px-1">name</code> matching an asset name exactly.
        </div>

        {/* Enrolled assets — show so user knows which UUIDs to use */}
        {enrolledAssets.length > 0 && (
          <div className="border border-ink-200 rounded-sm">
            <div className="bg-ink-50 px-3 py-2 text-xs font-medium text-ink-600 border-b border-ink-200 flex items-baseline justify-between">
              <span>Enrolled assets in this project ({enrolledAssets.length})</span>
              <span className="text-ink-400 font-normal">Copy an ID into your CBOM as the <code className="font-mono">qram:asset_id</code> property value</span>
            </div>
            <div className="max-h-40 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white border-b border-ink-100">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-ink-400 font-medium">Asset name</th>
                    <th className="text-left px-3 py-1.5 text-ink-400 font-medium">UUID (use as qram:asset_id)</th>
                  </tr>
                </thead>
                <tbody>
                  {enrolledAssets.map((a) => (
                    <tr key={a.id} className="border-b border-ink-50 hover:bg-ink-50">
                      <td className="px-3 py-1.5 text-ink-700 font-medium">{a.name}</td>
                      <td className="px-3 py-1.5 font-mono text-ink-500 text-[11px] select-all">{a.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {enrolledAssets.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-800">
            ⚠ No assets enrolled in this project yet. Import assets first (Import CSV on the project header), then import CBOM artifacts.
          </div>
        )}

        {/* Mode */}
        <div className="flex items-center gap-4 text-sm">
          <span className="font-medium text-ink-600">Mode:</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" checked={mode === 'append'} onChange={() => setMode('append')} />
            <span>Append <span className="text-xs text-ink-400">(add to existing Cbom-sourced artifacts)</span></span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} />
            <span>Replace <span className="text-xs text-ink-400">(delete all existing Cbom artifacts first)</span></span>
          </label>
        </div>

        {/* File upload */}
        <div
          className={`border-2 border-dashed rounded-sm p-6 text-center transition-colors ${
            hasFile ? 'border-green-300 bg-green-50' : 'border-ink-200 bg-ink-50 hover:border-blue-400'
          }`}
        >
          {hasFile ? (
            <div className="space-y-2">
              <div className="text-xl">✓</div>
              <div className="text-sm font-medium text-green-700">{fileName}</div>
              <div className="text-xs text-ink-500">CycloneDX JSON ready to import</div>
              <button
                type="button"
                onClick={() => { cbomBodyRef.current = ''; setFileName(null); setSummary(null); setError(null); }}
                className="text-xs text-ink-400 hover:text-ink-700 underline"
              >Remove file</button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-2xl text-ink-300">↑</div>
              <div className="text-sm text-ink-500">Select a CycloneDX JSON (.json) file</div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm border border-blue-300 rounded px-4 py-1.5 text-blue-700 hover:border-blue-500 transition-colors"
              >Choose file…</button>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept=".json,application/json,text/plain" className="hidden" onChange={handleFileUpload} />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-xs text-risk-critical">
            {error}
          </div>
        )}

        {/* Result */}
        {summary && (
          <div className="border border-ink-200 rounded-sm overflow-hidden">
            <div className={`px-3 py-2 text-xs font-medium ${summary.failed === 0 ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
              {summary.total_imported} artifact{summary.total_imported !== 1 ? 's' : ''} imported
              {summary.cbom_name ? ` from "${summary.cbom_name}"` : ''}
              {summary.failed > 0 ? ` · ${summary.failed} error${summary.failed !== 1 ? 's' : ''}` : ''}
            </div>
            <div className="grid grid-cols-6 divide-x divide-ink-100 text-center text-xs p-2">
              {[
                ['Keys', summary.imported_keys],
                ['Certificates', summary.imported_certificates],
                ['Protocols', summary.imported_protocols],
                ['Cipher suites', summary.imported_cipher_suites],
                ['Libraries', summary.imported_libraries],
                ['SSH algorithms', summary.imported_ssh_algorithms ?? 0],
              ].map(([label, count]) => (
                <div key={String(label)} className="px-2">
                  <div className="font-mono text-lg text-ink-800">{String(count)}</div>
                  <div className="text-ink-400">{String(label)}</div>
                </div>
              ))}
            </div>
            {(summary.devices ?? 0) > 0 && (
              <div className="px-3 pb-2 text-xs text-ink-600" data-testid="cbom-devices">
                {summary.devices_matched} of {summary.devices} assets in the CBOM matched this project's assets (by name, hostname, IP or external ref)
                {(summary.imported_unlinked ?? 0) > 0 ? ` · ${summary.imported_unlinked} artifacts kept unlinked` : ''}
                {(summary.devices_unmatched?.length ?? 0) > 0 && (
                  <div className="mt-1 text-ink-400" data-testid="cbom-devices-unmatched">
                    Not matched: {summary.devices_unmatched!.join(', ')}
                    {(summary.devices ?? 0) - (summary.devices_matched ?? 0) > summary.devices_unmatched!.length ? ', …' : ''}
                  </div>
                )}
              </div>
            )}
            {summary.skipped > 0 && (
              <div className="px-3 pb-2 text-xs text-ink-400">
                {summary.skipped} component{summary.skipped !== 1 ? 's' : ''} skipped (not a cryptographic artifact)
              </div>
            )}
            {summary.errors.length > 0 && (
              <div className="border-t border-red-100 px-3 py-2">
                <div className="text-xs font-medium text-risk-critical mb-1">Errors</div>
                <ul className="space-y-0.5 text-xs font-mono text-risk-critical">
                  {summary.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>{e.component}: {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between items-center pt-2 border-t border-ink-100">
          <button type="button" onClick={handleClose} className="text-sm text-ink-500 hover:text-ink">
            Close
          </button>
          <button
            type="button"
            disabled={!hasFile || importMutation.isPending}
            onClick={() => importMutation.mutate()}
            className="text-sm bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {importMutation.isPending ? 'Importing…' : 'Import CBOM'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
